import { useContext, useEffect, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  Block,
  Button,
  FlexContainer,
  H4,
  InputText,
  Modal,
  SoundNotificationContext,
  Span,
  useNotificationsV2,
} from '@uni-space/uni-components';
import { useTranslation } from 'react-i18next';

import { createAcceptanceAct, searchManifests } from '../api';
import {
  CreateAcceptanceActHttpResponse,
  CreateAcceptanceActStatus,
  ManifestDto,
  ManifestStatus,
  normalizeManifestStatus,
} from '../model/manifestAcceptance';
import { ErrorDTO } from '@shared/types/api';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** Приёмка создана — родитель открывает экран сканирования */
  onCreated: (act: CreateAcceptanceActHttpResponse) => void;
  onRefetch?: () => void;
};

type SearchState =
  | { kind: 'idle' }
  | { kind: 'searching' }
  | { kind: 'suggestions' }
  | { kind: 'notFound' }
  | { kind: 'found'; manifest: ManifestDto }
  | { kind: 'alreadyAccepting'; manifest: ManifestDto }
  | { kind: 'invalidStatus'; manifest: ManifestDto };

const MIN_QUERY_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;

const COLOR_OK = '#1d6b3b';
const COLOR_WARN = '#8a5a00';
const COLOR_ERROR = '#9f1f1f';
const COLOR_MUTED = '#666';

/** Один манифест — одна приёмка: закрытый и уже принимаемый принять заново нельзя */
const evaluateManifest = (manifest: ManifestDto): SearchState => {
  const status = normalizeManifestStatus(manifest.manifestStatus);
  if (status === ManifestStatus.Closed) return { kind: 'invalidStatus', manifest };
  if (manifest.acceptanceActId) return { kind: 'alreadyAccepting', manifest };
  if (status !== ManifestStatus.OnTheWay) return { kind: 'invalidStatus', manifest };
  return { kind: 'found', manifest };
};

/**
 * Приёмка создаётся с манифестом или без: манифест ищется по началу номера, система подсвечивает,
 * найден ли он и можно ли по нему принимать. Непустой, но не найденный номер блокирует создание —
 * чтобы опечатка не превратилась в приёмку без манифеста.
 */
export default function CreateAcceptanceAct({ isOpen, onClose, onCreated, onRefetch }: Props) {
  const { t } = useTranslation();
  const { createNotification } = useNotificationsV2();
  const { onSoundSuccess, onSoundError } = useContext(SoundNotificationContext);
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [manifestQuery, setManifestQuery] = useState('');
  const [suggestions, setSuggestions] = useState<ManifestDto[]>([]);
  const [search, setSearch] = useState<SearchState>({ kind: 'idle' });

  useEffect(() => {
    if (!isOpen) {
      setVehicleNumber('');
      setManifestQuery('');
      setSuggestions([]);
      setSearch({ kind: 'idle' });
    }
  }, [isOpen]);

  useEffect(() => {
    const query = manifestQuery.trim();
    if (query.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setSearch({ kind: 'idle' });
      return;
    }

    const controller = new AbortController();
    setSearch({ kind: 'searching' });

    const timer = setTimeout(async () => {
      try {
        const res = await searchManifests(query, 8, { signal: controller.signal });
        const items = res.items ?? [];
        const exact = items.find((m) => m.number.toLowerCase() === query.toLowerCase());

        if (exact) {
          setSuggestions([]);
          setSearch(evaluateManifest(exact));
          return;
        }

        setSuggestions(items);
        setSearch(items.length > 0 ? { kind: 'suggestions' } : { kind: 'notFound' });
      } catch {
        if (!controller.signal.aborted) {
          setSuggestions([]);
          setSearch({ kind: 'notFound' });
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [manifestQuery]);

  const { mutateAsync: onCreate, isPending } = useMutation({
    mutationKey: ['create-acceptance-act'],
    mutationFn: createAcceptanceAct,
    onSuccess: (res) => {
      switch (res.status) {
        case CreateAcceptanceActStatus.Success:
          createNotification({
            type: 'success',
            message: {
              title: t('manifestAcceptance.notifications.actCreated.title'),
              desc: t('manifestAcceptance.notifications.actCreated.desc', { number: res.number ?? '' }),
            },
          });
          onSoundSuccess?.();
          onRefetch?.();
          onClose();
          onCreated(res);
          return;

        case CreateAcceptanceActStatus.HasErrorApiIntegration:
          // Приёмка создана, но SortCenter API не подтвердил манифест — работать можно, предупреждаем.
          createNotification({
            type: 'warning',
            message: {
              title: t('manifestAcceptance.notifications.apiIntegration.title'),
              desc: t('manifestAcceptance.notifications.apiIntegration.desc'),
            },
          });
          onRefetch?.();
          onClose();
          onCreated(res);
          return;

        case CreateAcceptanceActStatus.ManifestAlreadyAccepting:
          createNotification({
            type: 'warning',
            message: {
              title: t('manifestAcceptance.notifications.createRejected.title'),
              desc: t('manifestAcceptance.acceptanceActs.manifestSearch.alreadyAccepting', {
                number: res.existingAcceptanceNumber ?? '',
              }),
            },
          });
          onSoundError?.();
          return;

        default:
          createNotification({
            type: 'warning',
            message: {
              title: t('manifestAcceptance.notifications.createRejected.title'),
              desc: t(`manifestAcceptance.acceptanceActs.createStatus.${CreateAcceptanceActStatus[res.status]}`),
            },
          });
          onSoundError?.();
      }
    },
    onError: (e: AxiosError<ErrorDTO>) => {
      createNotification({
        type: 'error',
        message: {
          title: t('common.systemError.title'),
          desc: e.response?.data.message ?? t('common.systemError.desc'),
        },
      });
      onSoundError?.();
    },
  });

  const trimmedVehicleNumber = vehicleNumber.trim();
  const trimmedQuery = manifestQuery.trim();
  const isWithoutManifest = trimmedQuery.length === 0;
  const canSubmit = !!trimmedVehicleNumber && (isWithoutManifest || search.kind === 'found') && !isPending;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onCreate({
      vehicleNumber: trimmedVehicleNumber,
      manifestNumber: search.kind === 'found' ? search.manifest.number : null,
    });
  };

  const renderSearchHint = () => {
    switch (search.kind) {
      case 'idle':
        return (
          <Span color={COLOR_MUTED}>
            {isWithoutManifest
              ? t('manifestAcceptance.acceptanceActs.manifestSearch.empty')
              : t('manifestAcceptance.acceptanceActs.manifestSearch.hint', { count: MIN_QUERY_LENGTH })}
          </Span>
        );
      case 'searching':
        return <Span color={COLOR_MUTED}>{t('manifestAcceptance.acceptanceActs.manifestSearch.searching')}</Span>;
      case 'suggestions':
        return <Span color={COLOR_MUTED}>{t('manifestAcceptance.acceptanceActs.manifestSearch.suggestions')}</Span>;
      case 'notFound':
        return <Span color={COLOR_ERROR}>{t('manifestAcceptance.acceptanceActs.manifestSearch.notFound')}</Span>;
      case 'found':
        return (
          <Span color={COLOR_OK} fw={600}>
            {t('manifestAcceptance.acceptanceActs.manifestSearch.found', { count: search.manifest.bagsCount ?? 0 })}
          </Span>
        );
      case 'alreadyAccepting':
        return (
          <Span color={COLOR_WARN} fw={600}>
            {t('manifestAcceptance.acceptanceActs.manifestSearch.alreadyAccepting', {
              number: search.manifest.acceptanceNumber ?? '',
            })}
          </Span>
        );
      case 'invalidStatus':
        return (
          <Span color={COLOR_ERROR} fw={600}>
            {t('manifestAcceptance.acceptanceActs.manifestSearch.invalidStatus', {
              status: t(
                `manifestAcceptance.status.${normalizeManifestStatus(search.manifest.manifestStatus) ?? ManifestStatus.OnTheWay}`,
              ),
            })}
          </Span>
        );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <FlexContainer direction="column" gap="16px">
        <Block>
          <H4 align="center">{t('manifestAcceptance.acceptanceActs.create.title')}</H4>
        </Block>
        <Block>
          <InputText
            id="AcceptanceActVehicleNumber"
            label={t('manifestAcceptance.acceptanceActs.create.vehicleNumberLabel')}
            value={vehicleNumber}
            onChange={setVehicleNumber}
          />
        </Block>
        <Block>
          <InputText
            id="AcceptanceActManifestNumber"
            label={t('manifestAcceptance.acceptanceActs.create.manifestLabel')}
            value={manifestQuery}
            onChange={setManifestQuery}
            autoComplete="off"
          />
          <Block mt={6}>{renderSearchHint()}</Block>
          {search.kind === 'suggestions' && suggestions.length > 0 && (
            <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0, border: '1px solid #ddd', borderRadius: 8 }}>
              {suggestions.map((manifest) => {
                const status = normalizeManifestStatus(manifest.manifestStatus) ?? ManifestStatus.OnTheWay;
                return (
                  <li key={manifest.number}>
                    <button
                      type="button"
                      onClick={() => setManifestQuery(manifest.number)}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '8px 12px',
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        fontSize: 14,
                      }}
                    >
                      <strong>{manifest.number}</strong>
                      {' · '}
                      {t('manifestAcceptance.acceptanceActs.manifestSearch.bags', { count: manifest.bagsCount ?? 0 })}
                      {' · '}
                      {t(`manifestAcceptance.status.${status}`)}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Block>
        <Button onClick={handleSubmit} loading={isPending} disabled={!canSubmit}>
          {isWithoutManifest
            ? t('manifestAcceptance.acceptanceActs.create.submitWithoutManifest')
            : t('manifestAcceptance.acceptanceActs.create.submit')}
        </Button>
      </FlexContainer>
    </Modal>
  );
}
